import { Switch, type SwitchProps } from '@progress/kendo-react-inputs'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useInvalidateBrandAccountAiEnabledQuery } from '~/hooks/useBrandAccountAiEnabledQuery'
import { useNotify } from '~/hooks/useNotify'
import { useNotifyOnError } from '~/hooks/useNotifyOnError'

import { useSetBrandAccountAiEnabledMutation } from '../hooks/useSetBrandAccountAiEnabledMutation'

export type AiToggleSectionProps = {
  aiEnabled: boolean
  brandAccountId: string
  canEdit: boolean
}

export const AiToggleSection = ({
  aiEnabled,
  brandAccountId,
  canEdit,
}: AiToggleSectionProps) => {
  const { t } = useTranslation()
  const notify = useNotify()
  const notifyError = useNotifyOnError()
  const invalidateAiEnabled =
    useInvalidateBrandAccountAiEnabledQuery(brandAccountId)

  const mutation = useSetBrandAccountAiEnabledMutation({
    onSuccess: () => {
      notify({ content: t('ai_setting_saved'), style: 'success' })
      void invalidateAiEnabled()
    },
    onError: (error) => {
      notifyError(error, {
        403: t('no_permission_to_change_ai_settings'),
      })
    },
  })

  const handleSwitchChange = React.useCallback<EH<SwitchProps, 'onChange'>>(
    (event) => {
      mutation.mutate({ brandAccountId, aiEnabled: event.target.value })
    },
    [brandAccountId, mutation],
  )

  return (
    <LabelingContainer label={t('ai_function')}>
      <VStack align="start" spacing={1}>
        <Switch
          checked={aiEnabled}
          disabled={!canEdit || mutation.isPending}
          offLabel={t('off')}
          onChange={handleSwitchChange}
          onLabel={t('on')}
        />
        {/* 変更権限を持たないユーザーには、AIがOFFの時だけ利用不可の旨を
            案内する（ONの時は変更する必要がなく、案内不要のため）。 */}
        {!canEdit && !aiEnabled && (
          <Typography variant="subscript">
            {t('ai_disabled_message')}
          </Typography>
        )}
      </VStack>
    </LabelingContainer>
  )
}
